import React from 'react';

/*
 * Footer techscape in a toile / copperplate style: single-ink NYC
 * vignettes engraved with line fills (horizontal for land and water,
 * diagonal for shadow faces, lattice for windows), plus a fine dotted
 * network between landmark tips. Static geometry is computed once with a
 * seeded PRNG; only nodes, packets, ripples and the tug animate.
 *
 * viewBox 1600 x 300 plus 16 units of headroom, anchored bottom-center. Phones see roughly x 460-1140,
 * so the bridge, Empire State, Chrysler and One WTC live there.
 */

const VB_W = 1600;
const VB_H = 300;
/** Extra sky above y=0 so hub rings and arcs never touch the crop edge. */
const HEADROOM = 16;
const HORIZON = 212;
const LAND = 264;
const INK = '#a9dcea';
const PAPER = '#06131d';

function mulberry32(seed) {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const f = (n) => Math.round(n * 10) / 10;
const rect = (x, y, w, h) => `M${x} ${y}h${w}v${h}h${-w}z`;

/* ---------- Background ---------- */

const HILLS = [
  `M0 198 C120 182 220 152 340 144 C440 138 520 160 600 168 C680 176 720 150 800 134`,
  `C880 118 960 150 1040 160 C1120 170 1200 132 1300 128 C1400 124 1480 160 1600 172 V${HORIZON} H0 Z`
].join(' ');

const FAR_SKYLINE = (() => {
  const rand = mulberry32(31);
  const blocks = [];
  for (let x = 440; x < 1270; ) {
    const w = 10 + Math.round(rand() * 16);
    const peak = Math.exp(-((x - 820) ** 2) / (2 * 220 * 220));
    blocks.push({ x, w, h: Math.round(6 + peak * 22 + rand() * 12) });
    x += w;
  }
  let d = `M${blocks[0].x} ${HORIZON}`;
  blocks.forEach((b) => {
    d += ` V${HORIZON - b.h} H${b.x + b.w}`;
  });
  return `${d} V${HORIZON} Z`;
})();

/* ---------- River ---------- */

const RIVER = [
  `M846 214 C800 224 800 240 900 250 C1010 262 1060 270 1000 284 C940 296 700 296 500 300`,
  `H1150 C1214 288 1236 262 1172 248 C1110 234 1000 224 926 214 Z`
].join(' ');

const RIPPLES = [
  [920, 253, 14], [990, 258, 12], [1120, 250, 10], [1090, 286, 16], [860, 294, 18],
  [700, 298, 14], [1010, 296, 12], [1160, 266, 10], [960, 238, 10]
]
  .map(([x, y, w]) => `M${x} ${y}q${w / 4} -2 ${w / 2} 0t${w / 2} 0`)
  .join('');

/* ---------- Far shore landmarks ---------- */

/** One building tier: lit front with vertical piers, shaded right strip. */
const tier = (x1, x2, y1, y2) => {
  const s = Math.round((x2 - x1) * 0.28);
  return {
    front: rect(x1, y1, x2 - x1 - s, y2 - y1),
    shade: rect(x2 - s, y1, s, y2 - y1),
    outline: rect(x1, y1, x2 - x1, y2 - y1)
  };
};

const ESB = [
  tier(536, 624, 196, HORIZON),
  tier(548, 612, 96, 196),
  tier(556, 604, 84, 96),
  tier(564, 596, 74, 84),
  tier(570, 590, 64, 74),
  tier(574, 586, 52, 64)
];
const ESB_SPIRE = 'M576 52 L578 40 Q580 33 582 40 L584 52 Z M580 33 V24';

const CHRYSLER = tier(676, 704, 110, HORIZON);
const CHRYSLER_CROWN = [[14, 110], [11, 101], [8, 93], [5, 86]]
  .map(([r, y]) => `M${690 - r} ${y} A${r} ${r} 0 0 1 ${690 + r} ${y} Z`)
  .join(' ');
const CHRYSLER_SPIRE = 'M687 86 L690 60 L693 86 Z';

const WTC_BASE = rect(992, 190, 36, HORIZON - 190);
const WTC_LEFT = 'M992 190 L1002 52 H1010 V190 Z';
const WTC_BODY = 'M992 190 L1002 52 H1018 L1028 190 Z';
const WTC_FACETS = 'M992 190 L1018 52 M1028 190 L1002 52';
const WTC_TOP = `${rect(1000, 46, 20, 6)} M1010 46 V24`;

/* ---------- Brooklyn Bridge ---------- */

const DECK_Y = 236;
const TOWERS = [790, 1150];
const TOWER_TOP = 166;
const qPoint = (p0, c, p1, t) => [
  (1 - t) ** 2 * p0[0] + 2 * (1 - t) * t * c[0] + t * t * p1[0],
  (1 - t) ** 2 * p0[1] + 2 * (1 - t) * t * c[1] + t * t * p1[1]
];
const SPANS = [
  [[690, 232], [750, 214], [790, TOWER_TOP + 4]],
  [[790, TOWER_TOP + 4], [970, 290], [1150, TOWER_TOP + 4]],
  [[1150, TOWER_TOP + 4], [1200, 214], [1250, 232]]
];
const CABLES = SPANS.map(([a, c, b]) => `M${a[0]} ${a[1]} Q${c[0]} ${c[1]} ${b[0]} ${b[1]}`).join(' ');
const SUSPENDERS = SPANS.map(([a, c, b]) => {
  let d = '';
  const n = Math.round((b[0] - a[0]) / 9);
  for (let i = 1; i < n; i += 1) {
    const [x, y] = qPoint(a, c, b, i / n);
    if (DECK_Y - y > 2) d += `M${f(x)} ${f(y)}V${DECK_Y}`;
  }
  return d;
}).join('');
const STAYS = TOWERS.map((tx) =>
  [24, 44, 64, 84]
    .map((dx) => `M${tx} ${TOWER_TOP + 8}L${tx - dx} ${DECK_Y}M${tx} ${TOWER_TOP + 8}L${tx + dx} ${DECK_Y}`)
    .join('')
).join('');
const towerBody = (x) => rect(x - 13, TOWER_TOP, 26, 252 - TOWER_TOP);
const towerShade = (x) => rect(x + 6, TOWER_TOP, 7, 252 - TOWER_TOP);
const towerCap = (x) => rect(x - 15, TOWER_TOP - 4, 30, 4);
const towerArches = (x) =>
  [x - 10, x + 3].map((a) => `M${a} ${DECK_Y} V186 Q${a + 3.5} 176 ${a + 7} 186 V${DECK_Y} Z`).join(' ');

/* ---------- Foreground vignettes ---------- */

const HOUSES = [
  { x: 170, top: 196 },
  { x: 230, top: 194 },
  { x: 290, top: 198 }
];
const houseParts = ({ x, top }) => {
  const w = 60;
  return {
    body: rect(x, top, w, LAND - top),
    cornice: rect(x - 2, top - 6, w + 4, 6),
    windows: [
      rect(x + 9, top + 12, 13, 13), rect(x + 36, top + 12, 13, 13),
      rect(x + 9, top + 32, 13, 13), rect(x + 36, top + 32, 13, 13),
      rect(x + 9, top + 52, 13, 8)
    ].join(''),
    sills: [top + 26, top + 46].map((y) => `M${x + 7} ${y}h17M${x + 34} ${y}h17`).join(''),
    door: rect(x + 37, top + 50, 11, LAND - 8 - (top + 50)),
    stoop: [
      rect(x + 33, LAND - 8, 19, 2.7),
      rect(x + 31, LAND - 5.3, 23, 2.7),
      rect(x + 29, LAND - 2.6, 27, 2.6),
      `M${x + 31} ${LAND - 16}L${x + 26} ${LAND}`
    ].join('')
  };
};
const ROW_SIDE = `M350 198 L364 205 V${LAND} H350 Z`;

const WATER_TOWER = {
  legs: 'M248 194 L251 178 M272 194 L269 178 M251 178 L272 194 M269 178 L248 194',
  tank: rect(246, 150, 28, 28),
  shade: rect(266, 150, 8, 28),
  hoops: 'M246 158 h28 M246 169 h28',
  roof: 'M243 150 L260 132 L277 150 Z',
  finial: 'M260 132 V126'
};

const LAMP = {
  post: `M132 ${LAND} V206 M127 ${LAND} h10 M129 ${LAND - 4} h6`,
  lantern: 'M126 194 h12 l-2 12 h-8 z',
  cap: 'M124 194 L132 187 L140 194 Z M132 187 V183'
};

const FLATIRON = (() => {
  const x0 = 1392;
  const x1 = 1402;
  const x2 = 1452;
  const base = 266;
  const top = 120;
  const slope = (x) => top + ((x - x1) / (x2 - x1)) * 8;
  let windows = '';
  for (let x = x1 + 4; x < x2 - 5; x += 8) {
    for (let y = slope(x) + 14; y < base - 10; y += 10) windows += rect(f(x), f(y), 4, 5);
  }
  return {
    prow: rect(x0, top, x1 - x0, base - top),
    face: `M${x1} ${top} L${x2} ${top + 8} V${base} H${x1} Z`,
    cornice: `M${x0 - 2} ${top - 5} H${x1} L${x2 + 2} ${top + 3} V${top + 8} L${x1} ${top} H${x0 - 2} Z`,
    windows
  };
})();

/** Leaf cluster: almond leaves pointing outward, sorted back to front. */
function leafCluster(cx, cy, rx, ry, count, seed) {
  const rand = mulberry32(seed);
  const leaves = [];
  while (leaves.length < count) {
    const u = rand() * 2 - 1;
    const v = rand() * 2 - 1;
    if (u * u + v * v <= 1) {
      const x = cx + u * rx;
      const y = cy + v * ry;
      const a = Math.atan2(v, u) + (rand() - 0.5) * 1.6;
      const len = 11 + rand() * 7;
      const wid = len * 0.42;
      const dx = (Math.cos(a) * len) / 2;
      const dy = (Math.sin(a) * len) / 2;
      const px = -Math.sin(a) * wid;
      const py = Math.cos(a) * wid;
      const d = [
        `M${f(x - dx)} ${f(y - dy)}Q${f(x + px)} ${f(y + py)} ${f(x + dx)} ${f(y + dy)}`,
        `Q${f(x - px)} ${f(y - py)} ${f(x - dx)} ${f(y - dy)}Z`,
        `M${f(x - dx)} ${f(y - dy)}L${f(x + dx * 0.8)} ${f(y + dy * 0.8)}`
      ].join('');
      leaves.push({ y, d, hatched: rand() < 0.3 });
    }
  }
  return leaves.sort((p, q) => p.y - q.y);
}

const circlePath = (cx, cy, r) => `M${f(cx + r)} ${f(cy)}A${r} ${r} 0 1 1 ${f(cx - r)} ${f(cy)}A${r} ${r} 0 1 1 ${f(cx + r)} ${f(cy)}`;
const rosette = (cx, cy) => {
  let d = '';
  for (let i = 0; i < 8; i += 1) {
    const a = (i / 8) * Math.PI * 2;
    d += circlePath(cx + Math.cos(a) * 5, cy + Math.sin(a) * 5, 2.4);
  }
  return d + circlePath(cx, cy, 2.6);
};

const TREES = [
  {
    key: 'park-w',
    trunk: `M426 ${LAND - 2} C428 246 424 236 430 224 M434 ${LAND - 2} C434 246 438 236 434 222 M430 236 L414 214 M434 232 L452 212`,
    leaves: leafCluster(430, 178, 56, 50, 120, 11),
    rosettes: [[412, 168], [452, 196]]
  },
  {
    key: 'park-e',
    trunk: `M1286 ${LAND} C1288 248 1284 236 1290 224 M1294 ${LAND} C1294 248 1298 236 1294 222 M1290 236 L1272 216 M1294 232 L1312 214`,
    leaves: leafCluster(1290, 178, 60, 50, 128, 23),
    rosettes: [[1306, 166], [1268, 194]]
  },
  { key: 'corner-w', trunk: '', leaves: leafCluster(36, 288, 84, 44, 70, 41), rosettes: [[40, 282]] },
  { key: 'corner-e', trunk: '', leaves: leafCluster(1570, 286, 88, 46, 74, 57), rosettes: [[1560, 280]] }
];

const BIRDS = [[330, 70], [346, 62], [1212, 46], [1228, 52]]
  .map(([x, y]) => `M${x} ${y}q3 -3 6 0q3 -3 6 0`)
  .join('');

/** Tug drawn around its waterline center (0, 0) so it can travel the river. */
const TUG = {
  hull: 'M-18 0 L18 0 L12 6 H-12 Z',
  cabin: `${rect(-10, -8, 14, 8)}${rect(-7, -6, 4, 3)}`,
  stack: rect(-2, -15, 4, 7),
  smoke: `${circlePath(-4, -19, 2)}${circlePath(-11, -25, 2.8)}${circlePath(-18, -30, 3.4)}`
};
/** Channel the tug follows: river mouth, under the bridge, around the bend toward the viewer. */
const TUG_ROUTE = 'M886 217 C930 226 990 236 1060 246 C1130 256 1162 268 1112 282 C1082 290 1046 292 1012 293';
const TUG_PARKED = 'translate(1100 268)';

/* ---------- Network ---------- */

/** Backbone across the rooftops: water tower, Empire State, One WTC, Flatiron. */
const NODES = [[260, 124], [580, 24], [1010, 24], [1402, 115]];
const HUB_INDEX = [1, 2];
/** Each link: node indexes plus its curve's control point. */
const LINKS = [
  { from: 0, to: 1, ctrl: [414, 66] },
  { from: 1, to: 2, ctrl: [795, 22] },
  { from: 2, to: 3, ctrl: [1336, 26] }
];
const linkPath = ({ from, to, ctrl }) => {
  const [x1, y1] = NODES[from];
  const [x2, y2] = NODES[to];
  return `M${x1} ${y1} Q${ctrl[0]} ${ctrl[1]} ${x2} ${y2}`;
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** SMIL ignores the CSS reduced-motion rule, so the tug is parked when motion is reduced. */
const Tug = () => {
  const body = (
    <g className="lp-toile-tug">
      <Ink d={TUG.hull} fill="url(#lp-toile-h-dense)" />
      <Ink d={TUG.cabin} fill={PAPER} />
      <Ink d={TUG.stack} fill="url(#lp-toile-diag)" />
      <Ink d={TUG.smoke} o={0.5} w={0.7} />
    </g>
  );
  if (prefersReducedMotion()) return <g transform={TUG_PARKED}>{body}</g>;
  return (
    <g>
      <animateMotion path={TUG_ROUTE} dur="34s" repeatCount="indefinite" />
      <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.08;0.9;1" dur="34s" repeatCount="indefinite" />
      <g>
        {/* Smaller near the horizon, full size as it rounds the bend toward the viewer */}
        <animateTransform
          attributeName="transform"
          type="scale"
          values="0.45;0.75;1.05;1.05"
          keyTimes="0;0.4;0.8;1"
          dur="34s"
          repeatCount="indefinite"
        />
        {body}
      </g>
    </g>
  );
};

/* ---------- Render ---------- */

const Ink = ({ d, fill = 'none', o = 0.85, w = 1 }) => (
  <path d={d} fill={fill} stroke={INK} strokeOpacity={o} strokeWidth={w} />
);
const Fill = ({ d, pattern }) => <path d={d} fill={`url(#lp-toile-${pattern})`} />;

/** Engraving line fill on an opaque ground, so fills also occlude what is behind. */
const LinePattern = ({ id, size, angle = 0, cross = false, weight = 0.6, opacity = 0.55 }) => (
  <pattern
    id={`lp-toile-${id}`}
    patternUnits="userSpaceOnUse"
    width={size}
    height={size}
    patternTransform={angle ? `rotate(${angle})` : undefined}
  >
    <rect width={size} height={size} fill={PAPER} />
    <line x1="0" y1={size / 2} x2={size} y2={size / 2} stroke={INK} strokeWidth={weight} strokeOpacity={opacity} />
    {cross && (
      <line x1={size / 2} y1="0" x2={size / 2} y2={size} stroke={INK} strokeWidth={weight} strokeOpacity={opacity} />
    )}
  </pattern>
);

const FooterTechscape = () => (
  <div className="lp-footer-techscape" aria-hidden="true">
    <svg
      className="lp-footer-techscape-svg"
      viewBox={`0 ${-HEADROOM} ${VB_W} ${VB_H + HEADROOM}`}
      preserveAspectRatio="xMidYMax slice"
      focusable="false"
    >
      <defs>
        <LinePattern id="h" size={3.4} opacity={0.5} weight={0.65} />
        <LinePattern id="h-dense" size={2.8} opacity={0.7} weight={0.7} />
        <LinePattern id="v" size={3} angle={90} opacity={0.4} />
        <LinePattern id="diag" size={3} angle={-45} opacity={0.6} />
        <LinePattern id="lattice" size={3.2} angle={45} cross weight={0.5} />
      </defs>

      {/* Hills and distant skyline */}
      <Fill d={HILLS} pattern="h" />
      <Ink d={HILLS} o={0.55} />
      <Ink d={FAR_SKYLINE} fill={PAPER} o={0.5} w={0.8} />

      {/* Far-shore landmarks */}
      {ESB.map((t) => (
        <g key={t.outline}>
          <Fill d={t.front} pattern="v" />
          <Fill d={t.shade} pattern="diag" />
          <Ink d={t.outline} />
        </g>
      ))}
      <Ink d={ESB_SPIRE} fill={PAPER} />
      <Fill d={CHRYSLER.front} pattern="v" />
      <Fill d={CHRYSLER.shade} pattern="diag" />
      <Ink d={CHRYSLER.outline} />
      <Ink d={CHRYSLER_CROWN} fill="url(#lp-toile-lattice)" />
      <Ink d={CHRYSLER_SPIRE} fill={PAPER} />
      <Ink d={WTC_BODY} fill={PAPER} />
      <Fill d={WTC_LEFT} pattern="diag" />
      <Ink d={WTC_BODY} />
      <Ink d={WTC_FACETS} o={0.5} w={0.7} />
      <Fill d={WTC_BASE} pattern="v" />
      <Ink d={WTC_BASE} />
      <Ink d={WTC_TOP} fill={PAPER} />

      {/* Shoreline and river */}
      <Ink d={`M0 ${HORIZON} H848 M924 ${HORIZON} H${VB_W}`} o={0.7} />
      <Fill d={RIVER} pattern="h-dense" />
      <Ink d={RIVER} />
      <g className="lp-toile-ripples">
        <Ink d={RIPPLES} o={0.7} w={0.8} />
      </g>

      {/* Tug, drawn under the bridge */}
      <Tug />

      {/* Brooklyn Bridge */}
      <Ink d={STAYS} o={0.35} w={0.5} />
      <Ink d={SUSPENDERS} o={0.45} w={0.5} />
      {TOWERS.map((x) => (
        <g key={`tower-${x}`}>
          <Fill d={towerBody(x)} pattern="v" />
          <Fill d={towerShade(x)} pattern="diag" />
          <Ink d={towerBody(x)} />
          <Ink d={towerCap(x)} fill={PAPER} />
          <Ink d={towerArches(x)} fill={PAPER} />
        </g>
      ))}
      <Ink d={rect(680, DECK_Y, 582, 5)} fill="url(#lp-toile-lattice)" />
      <Ink d={CABLES} w={1.1} />

      {/* Trees */}
      {TREES.map((t) => (
        <g key={t.key}>
          {t.trunk && <Ink d={t.trunk} w={1.1} />}
          {t.leaves.map((l) => (
            <Ink key={l.d} d={l.d} fill={l.hatched ? 'url(#lp-toile-diag)' : PAPER} w={0.8} />
          ))}
          {t.rosettes.map(([x, y]) => (
            <Ink key={`r-${x}-${y}`} d={rosette(x, y)} fill={PAPER} w={0.7} />
          ))}
        </g>
      ))}

      {/* Brownstone row with rooftop water tower */}
      <Ink d={WATER_TOWER.legs} w={0.8} />
      <Fill d={WATER_TOWER.tank} pattern="v" />
      <Fill d={WATER_TOWER.shade} pattern="diag" />
      <Ink d={WATER_TOWER.tank} />
      <Ink d={WATER_TOWER.hoops} o={0.7} />
      <Ink d={WATER_TOWER.roof} fill="url(#lp-toile-h-dense)" />
      <Ink d={WATER_TOWER.finial} />
      <Ink d={ROW_SIDE} fill="url(#lp-toile-diag)" />
      {HOUSES.map((h) => {
        const p = houseParts(h);
        return (
          <g key={`house-${h.x}`}>
            <Ink d={p.body} fill={PAPER} />
            <Ink d={p.cornice} fill="url(#lp-toile-v)" />
            <Ink d={p.windows} fill="url(#lp-toile-lattice)" w={0.8} />
            <Ink d={p.sills} o={0.7} />
            <Ink d={p.door} fill="url(#lp-toile-h-dense)" w={0.8} />
            <Ink d={p.stoop} fill={PAPER} w={0.8} />
          </g>
        );
      })}

      {/* Lamp post */}
      <Ink d={LAMP.post} />
      <Ink d={LAMP.lantern} fill="url(#lp-toile-lattice)" />
      <Ink d={LAMP.cap} fill={PAPER} />

      {/* Flatiron */}
      <Fill d={FLATIRON.prow} pattern="v" />
      <Ink d={FLATIRON.prow} />
      <Ink d={FLATIRON.face} fill={PAPER} />
      <Ink d={FLATIRON.windows} fill="url(#lp-toile-h-dense)" w={0.6} o={0.7} />
      <Ink d={FLATIRON.cornice} fill="url(#lp-toile-v)" />

      {/* Ground strokes and birds */}
      <Ink d={`M96 ${LAND} H386 M1236 ${LAND + 2} H1480`} o={0.7} />
      <Ink d={BIRDS} o={0.7} w={0.9} />

      {/* Network: fine dotted arcs with cyan accents */}
      <g fill="none" stroke={INK} strokeOpacity="0.45" strokeWidth="0.8" strokeDasharray="1 3" strokeLinecap="round">
        {LINKS.map((link) => (
          <path key={`l-${link.from}-${link.to}`} d={linkPath(link)} />
        ))}
      </g>
      <g fill="none" stroke="#3dc4e3">
        {LINKS.map((link, i) => (
          <path
            key={`p-${link.from}-${link.to}`}
            className={`lp-fpacket lp-fpacket${i % 3}`}
            d={linkPath(link)}
            pathLength="100"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        ))}
        {HUB_INDEX.map((i) => (
          <g key={`hub-${i}`}>
            <circle className="lp-fpulse" cx={NODES[i][0]} cy={NODES[i][1]} r="7" strokeOpacity="0.6" />
            <circle cx={NODES[i][0]} cy={NODES[i][1]} r="11" strokeOpacity="0.3" strokeDasharray="1 2.5" />
          </g>
        ))}
      </g>
      <g fill="#3dc4e3">
        {NODES.map(([cx, cy], i) => (
          <circle
            key={`n-${cx}-${cy}`}
            className={HUB_INDEX.includes(i) ? undefined : `lp-ftw${i % 3}`}
            cx={cx}
            cy={cy}
            r={HUB_INDEX.includes(i) ? 2.8 : 1.8}
          />
        ))}
      </g>
    </svg>
  </div>
);

export default FooterTechscape;
